import { differenceInCalendarDays, startOfDay } from 'date-fns';

/**
 * ONE-WEEK CLAMP (ADO #104914)
 *
 * The board shows at most one week, and the week it shows is owned by React —
 * zoom is a pixel-scale control only.
 *
 * `WEEK_DAYS` is the maximum, not a fixed size: a caller that deliberately asks
 * for a narrower window (SchedulerPage jumps to a day-either-side window around
 * an unassigned task's appointment) keeps it. What is forbidden is a window
 * WIDER than a week, and a window that moves on its own.
 */
export const WEEK_DAYS = 7;
const MINUTE_MS = 60 * 1000;

/** Tolerance for comparing the drawn axis against the intended window. */
const AXIS_MATCH_TOLERANCE_MS = MINUTE_MS;

export type DisplayWindow = { startDate: Date; endDate: Date };

export function addDaysTo(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Round a boundary up to the next local midnight unless it already is one.
 *
 * `autoAdjustTimeAxis` defaults to true and is never set, so Bryntum widens the
 * drawn axis out to whole `mainUnit` (day) boundaries by itself. Normalising both
 * ends of the intended window to midnight is what makes the clamp STABLE: an
 * intended window Bryntum would have rounded can never match the axis it
 * produces, and each correction would re-trigger the next — an infinite loop.
 */
function ceilToDay(date: Date): Date {
  const floored = startOfDay(date);
  return floored.getTime() === date.getTime() ? floored : addDaysTo(floored, 1);
}

/**
 * The window the calendar is allowed to draw: anchored on the start of
 * `startDate`'s day, never longer than a week, both ends on a day boundary.
 */
export function oneWeekWindow(startDate: Date, endDate: Date): DisplayWindow {
  const anchor = startOfDay(startDate);
  const maxEnd = addDaysTo(anchor, WEEK_DAYS);
  const requestedEnd = ceilToDay(endDate);
  // A degenerate or inverted range still has to produce a drawable axis.
  const end =
    requestedEnd.getTime() <= anchor.getTime()
      ? addDaysTo(anchor, 1)
      : requestedEnd;
  return {
    startDate: anchor,
    endDate: end.getTime() > maxEnd.getTime() ? maxEnd : end,
  };
}

/** True when the drawn axis already is the intended window. */
export function axisMatchesWindow(
  axisStart: Date | undefined,
  axisEnd: Date | undefined,
  target: DisplayWindow
): boolean {
  if (!axisStart || !axisEnd) return false;
  return (
    Math.abs(axisStart.getTime() - target.startDate.getTime()) <
      AXIS_MATCH_TOLERANCE_MS &&
    Math.abs(axisEnd.getTime() - target.endDate.getTime()) <
      AXIS_MATCH_TOLERANCE_MS
  );
}

export type MenuDateField = 'start' | 'end';

export type MenuDateRangeResult =
  | { kind: 'apply'; range: DisplayWindow; limited: boolean }
  | { kind: 'rejected'; reason: 'endNotAfterStart' };

/**
 * Resolve one edit of the header menu's Date range popup into the window to show.
 *
 * The popup applies each field as it changes, so the two fields are handled
 * differently (ADO #109914):
 *  - Start keeps the current span and slides the end with it, so moving to
 *    another week, month or year never lands the end before the start.
 *  - End is taken as the exclusive end the field displays (Start 10/01 with
 *    End 10/08 is Oct 1 - Oct 7). An end on or before the start is rejected;
 *    a span longer than a week is limited to its first `WEEK_DAYS`.
 *
 * Spans are counted in calendar days, not milliseconds, so a daylight-saving
 * change inside the window cannot move a boundary off midnight.
 */
export function resolveMenuDateRange({
  current,
  field,
  value,
}: {
  current: DisplayWindow;
  field: MenuDateField;
  value: Date;
}): MenuDateRangeResult {
  if (field === 'start') {
    const startDate = startOfDay(value);
    const currentSpan = differenceInCalendarDays(
      current.endDate,
      current.startDate
    );
    const spanDays = Math.min(Math.max(currentSpan, 1), WEEK_DAYS);
    return {
      kind: 'apply',
      range: { startDate, endDate: addDaysTo(startDate, spanDays) },
      limited: false,
    };
  }

  const startDate = startOfDay(current.startDate);
  const endDate = startOfDay(value);
  const spanDays = differenceInCalendarDays(endDate, startDate);
  if (spanDays <= 0) {
    return { kind: 'rejected', reason: 'endNotAfterStart' };
  }
  if (spanDays > WEEK_DAYS) {
    return {
      kind: 'apply',
      range: { startDate, endDate: addDaysTo(startDate, WEEK_DAYS) },
      limited: true,
    };
  }
  return { kind: 'apply', range: { startDate, endDate }, limited: false };
}

/** True when two windows cover the same days. */
export function sameWindow(a: DisplayWindow, b: DisplayWindow): boolean {
  return (
    a.startDate.getTime() === b.startDate.getTime() &&
    a.endDate.getTime() === b.endDate.getTime()
  );
}

export type MenuEditNotice = { title: string; message: string };

export type MenuEditPlan = {
  /** The window to move to, or null to leave the board where it is. */
  target: DisplayWindow | null;
  /** A warning to show the dispatcher, if any. */
  notice: MenuEditNotice | null;
};

const INVALID_RANGE_NOTICE: MenuEditNotice = {
  title: 'Invalid date range',
  message: 'The end date must be after the start date.',
};

const LIMITED_RANGE_NOTICE: MenuEditNotice = {
  title: 'Date range limited',
  message: `Scheduler View shows up to ${WEEK_DAYS} days. Showing the first ${WEEK_DAYS} days from the chosen start.`,
};

/**
 * Decide what one Date range popup edit does to the board shown as `current`.
 *
 * The move and the warning are independent: a limited range still moves the
 * board (unless it already shows those days) AND warns. Choosing the window
 * already shown is a no-op, so repeating an edit cannot refetch or flicker.
 */
export function planMenuEdit({
  current,
  field,
  value,
}: {
  current: DisplayWindow;
  field: MenuDateField;
  value: Date;
}): MenuEditPlan {
  const result = resolveMenuDateRange({ current, field, value });
  if (result.kind === 'rejected') {
    return { target: null, notice: INVALID_RANGE_NOTICE };
  }

  const target = oneWeekWindow(result.range.startDate, result.range.endDate);
  return {
    target: sameWindow(target, current) ? null : target,
    notice: result.limited ? LIMITED_RANGE_NOTICE : null,
  };
}
